import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchProjects, fetchProjectTemplates, cloneProject, updateProjectApi } from "../api/projects";
import theme from "../utils/Theme";
import GenericTable from "../components/GenericTable";
import { Edit, Delete } from "@mui/icons-material";
import { CreateProjectCategoryDialog } from "../components/dialogs/CreateProjectPhaseDialog";
import CreateProjectDialog from "../components/dialogs/CreateProjectDialog";
import ProjectDetailsPanel from "../components/ProjectDetailsPanel";
import { sleep } from "../utils/utilFunctions";


const Projects = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [newProjectAmount, setNewProjectAmount] = useState("");

  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [tableSelectedProject, setTableSelectedProject] = useState(null);

  useEffect(() => {
    if (tableSelectedProject) {
      const selectedRow = projects.find((row) => row.id === tableSelectedProject.id);
      if (selectedRow) {
        setTableSelectedProject(selectedRow);
      }
    }
  }, [projects])

  const scrollToDetails = async () => {
    await sleep(100);
    if (tableSelectedProject) {
      window.scrollBy({
        top: 500,
        behavior: 'smooth'
      });
    }
  };

  const loadProjects = async () => {
    try {
      const data = await fetchProjects();
      setProjects(data);
      if (!tableSelectedProject) {
        setTableSelectedProject(data[0]);
      }
    } catch (err) {
      setError(t("errors.fetch_projects_failed"));
    }
  };

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templateData = await fetchProjectTemplates();
        setTemplates(templateData);
      } catch (err) {
        setError(t("errors.fetch_templates_failed"));
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
    loadTemplates();
  }, [t]);

  const handleCloneProject = async () => {
    try {
      await cloneProject(selectedTemplateId, newProjectTitle, newProjectAmount);
      setError(null);
      setDialogOpen(false);
      const data = await fetchProjects();
      setProjects(data);
      setNewProjectTitle("");
      setNewProjectAmount("");
      setSelectedTemplateId(null);
    } catch (err) {
      setError(t("errors.clone_project_failed"));
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewProjectTitle("");
    setNewProjectAmount("");
    setSelectedTemplateId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error" align="center">{error}</Typography>;
  }

  const updateProject = async (row, field, oldValue, newValue) => {
    const updatedProjectData = { [field]: newValue };

    try {
      const updatedProject = await updateProjectApi(row.id, updatedProjectData);
      const data = await fetchProjects();
      setProjects(data);
    } catch (err) {
      setError(t("errors.update_project_failed"));
    }
  };

  const handleCreateProject = async () => {
    setSelectedProject(null)
    const data = await fetchProjects();
    setProjects(data);
  }

  return (
    <Box sx={{ backgroundColor: 'white', padding: 2, minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {t("projects.active_projects")}
        </Typography>
        <Box>
          <Button
            onClick={handleOpenDialog}
            variant="contained"
            sx={{
              marginRight: "15px",
              bgcolor: "#444",
              color: "#fff",
              fontWeight: 500,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { bgcolor: "#333" },
            }}
          >
            {t("projects.clone_from_template")}
          </Button>
          <Button
            onClick={() => { setCreateProjectDialogOpen(true) }}
            variant="contained"
            sx={{
              bgcolor: "#444",
              color: "#fff",
              fontWeight: 500,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { bgcolor: "#333" },
            }}
          >
            {t("projects.create_project")}
          </Button>
        </Box>
      </Box>
      <GenericTable
        data={projects}
        columns={[
          { field: "title", label: t("fields.title"), type: "title"},
          { field: "client.name", label: t("common.client") },
          { field: "deadline", type: "date", label: t("fields.deadline") },
          { field: "total_time_logged", type: "hours", label: t("fields.total_time_logged") },
          {
            field: "status",
            label: t("fields.status"),
            type: "status",
            editable: true,
            options: [
              { value: "discussion", label: t("statuses.discussion") },
              { value: "signed", label: t("statuses.signed") },
              { value: "started", label: t("statuses.started") },
              { value: "in_progress", label: t("statuses.in_progress") },
              { value: "completed", label: t("statuses.completed") },
              { value: "on_hold", label: t("statuses.on_hold") },
              { value: "cancelled", label: t("statuses.cancelled") },
              { value: "needs_review", label: t("statuses.needs_review") },
              { value: "approved", label: t("statuses.approved") },
              { value: "archived", label: t("statuses.archived") }
            ]
          },
          { field: "amount", label: t("fields.amount") }
        ]}
        actions={[
          { icon: <Edit />, onClick: (row) => { setSelectedProject(row); setCreateProjectDialogOpen(true) } },
          { icon: <Delete />, onClick: (row) => console.log("Delete", row) }
        ]}
        onUpdate={updateProject}
        selectedElement={tableSelectedProject}
        onSelect={(row) => { scrollToDetails(); setTableSelectedProject(row) }}
      />

      {tableSelectedProject && (
        <Box sx={{ marginTop: 3, paddingBottom: 3 }}>
          <ProjectDetailsPanel selectedProject={tableSelectedProject} projectUpdated={loadProjects} />
        </Box>
      )}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: "#000" }}>{t("projects.clone_project")}</DialogTitle>
        <DialogContent sx={{ paddingTop: "5px !important" }}>
          <TextField
            fullWidth
            label={t("projects.new_project_title")}
            variant="outlined"
            value={newProjectTitle}
            onChange={(e) => setNewProjectTitle(e.target.value)}
            sx={{ marginBottom: "16px" }}
            InputLabelProps={{
              sx: { color: "#666", "&.Mui-focused": { color: "#000" } },
            }}
            InputProps={{
              sx: {
                "& input, & .MuiSelect-select": { color: "#000" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
              },
            }}
          />
          <TextField
            fullWidth
            label={t("projects.amount")}
            variant="outlined"
            type="number"
            value={newProjectAmount}
            onChange={(e) => setNewProjectAmount(e.target.value)}
            sx={{ marginBottom: "16px" }}
            InputLabelProps={{
              sx: { color: "#666", "&.Mui-focused": { color: "#000" } },
            }}
            InputProps={{
              sx: {
                "& input": { color: "#000" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
              },
            }}
          />
          <FormControl fullWidth sx={{ marginBottom: "16px" }}>
            <InputLabel sx={{ color: "#666", "&.Mui-focused": { color: "#000" } }}>{t("projects.template")}</InputLabel>
            <Select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              label={t("projects.template")}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#999" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
                "& .MuiSelect-select": { color: "#000" },
              }}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: 'black' }}>{t("actions.cancel")}</Button>
          <Button onClick={handleCloneProject} sx={{ bgcolor: "#444", color: "#fff", fontWeight: 500, "&:hover": { bgcolor: "#333" } }} variant="contained">
            {t("actions.clone")}
          </Button>
        </DialogActions>
      </Dialog>
      <CreateProjectDialog project={selectedProject} open={createProjectDialogOpen} onClose={() => { setSelectedProject(null); setCreateProjectDialogOpen(false) }} onSuccess={handleCreateProject} />
    </Box >
  );
};

export default Projects;